import type { Analytics, ConsentStatusString } from 'firebase/analytics';
import {
  getFirebasePublicConfig,
  isAnalyticsDebugEnabled,
  isFirebaseAnalyticsEnabled,
} from './config';
import type { AnalyticsEventParams } from './event-map';
import {
  getCurrentAnalyticsPageContext,
  type AnalyticsPageContext,
} from './page-context';

type FirebaseModules = {
  getApps: typeof import('firebase/app').getApps;
  initializeApp: typeof import('firebase/app').initializeApp;
  initializeAnalytics: typeof import('firebase/analytics').initializeAnalytics;
  isSupported: typeof import('firebase/analytics').isSupported;
  logEvent: typeof import('firebase/analytics').logEvent;
  setAnalyticsCollectionEnabled: typeof import('firebase/analytics').setAnalyticsCollectionEnabled;
  setConsent: typeof import('firebase/analytics').setConsent;
  setDefaultEventParameters: typeof import('firebase/analytics').setDefaultEventParameters;
};

let analyticsInstance: Analytics | null = null;
let analyticsInitPromise: Promise<Analytics | null> | null = null;
let modulesPromise: Promise<FirebaseModules> | null = null;
let logEventRef: FirebaseModules['logEvent'] | null = null;
let desiredConsentStatus: ConsentStatusString | null = null;

const loadFirebaseModules = (): Promise<FirebaseModules> => {
  if (!modulesPromise) {
    modulesPromise = Promise.all([import('firebase/app'), import('firebase/analytics')]).then(
      ([appModule, analyticsModule]) => ({
        getApps: appModule.getApps,
        initializeApp: appModule.initializeApp,
        initializeAnalytics: analyticsModule.initializeAnalytics,
        isSupported: analyticsModule.isSupported,
        logEvent: analyticsModule.logEvent,
        setAnalyticsCollectionEnabled:
          analyticsModule.setAnalyticsCollectionEnabled,
        setConsent: analyticsModule.setConsent,
        setDefaultEventParameters: analyticsModule.setDefaultEventParameters,
      })
    );
  }

  return modulesPromise;
};

const isDebugModeRequested = (): boolean => {
  if (isAnalyticsDebugEnabled()) {
    return true;
  }

  const debugMode = new URLSearchParams(window.location.search).get('debug_mode');
  return debugMode === '1' || debugMode === 'true';
};

const getOrCreateFirebaseApp = (
  modules: FirebaseModules,
  config: NonNullable<ReturnType<typeof getFirebasePublicConfig>>
) => modules.getApps()[0] ?? modules.initializeApp(config);

const getConsentSettings = (analyticsStorage: ConsentStatusString) => ({
  analytics_storage: analyticsStorage,
  ad_storage: 'denied' as const,
  ad_user_data: 'denied' as const,
  ad_personalization: 'denied' as const,
  personalization_storage: 'denied' as const,
});

export const getFirebaseAnalytics = (): Promise<Analytics | null> => {
  if (!isFirebaseAnalyticsEnabled() || typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (analyticsInstance) {
    return Promise.resolve(analyticsInstance);
  }

  if (analyticsInitPromise) {
    return analyticsInitPromise;
  }

  analyticsInitPromise = (async () => {
    const config = getFirebasePublicConfig();
    if (!config) {
      return null;
    }

    const modules = await loadFirebaseModules();
    if (!(await modules.isSupported())) {
      return null;
    }

    if (
      desiredConsentStatus === 'denied' ||
      !isFirebaseAnalyticsEnabled()
    ) {
      return null;
    }

    modules.setConsent(getConsentSettings('granted'));

    const app = getOrCreateFirebaseApp(modules, config);
    logEventRef = modules.logEvent;
    const safePageContext = getCurrentAnalyticsPageContext();
    analyticsInstance = modules.initializeAnalytics(app, {
      config: {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        ...safePageContext,
        ...(isDebugModeRequested() ? { debug_mode: true } : {}),
      },
    });
    modules.setDefaultEventParameters(safePageContext);

    return analyticsInstance;
  })().catch(() => null);

  return analyticsInitPromise;
};

export const logFirebaseEvent = async (
  eventName: string,
  params?: AnalyticsEventParams
): Promise<void> => {
  const analytics = await getFirebaseAnalytics();
  if (!analytics || !logEventRef) {
    return;
  }

  logEventRef(analytics, eventName, params);
};

export const setFirebaseDefaultPageContext = async (
  pageContext: AnalyticsPageContext
): Promise<void> => {
  const analytics = await getFirebaseAnalytics();
  if (!analytics || !modulesPromise) {
    return;
  }

  const modules = await modulesPromise;
  modules.setDefaultEventParameters(pageContext);
};

const expireAnalyticsCookie = (name: string, domain?: string): void => {
  const domainAttribute = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Max-Age=0; Path=/${domainAttribute}; SameSite=Lax`;
};

export const clearFirebaseAnalyticsCookies = (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const analyticsCookieNames = document.cookie
    .split(';')
    .map(cookie => cookie.trim().split('=', 1)[0])
    .filter(name => name.startsWith('_ga'));

  for (const name of analyticsCookieNames) {
    expireAnalyticsCookie(name);
    expireAnalyticsCookie(name, window.location.hostname);
    expireAnalyticsCookie(name, '.bundledrop.app');
  }
};

export const setFirebaseAnalyticsConsent = async (
  status: ConsentStatusString
): Promise<void> => {
  desiredConsentStatus = status;

  if (status === 'denied' && !modulesPromise && !analyticsInstance) {
    clearFirebaseAnalyticsCookies();
    return;
  }

  try {
    const modules = await loadFirebaseModules();
    modules.setConsent(getConsentSettings(status));

    if (status === 'denied') {
      if (analyticsInitPromise) {
        await analyticsInitPromise;
      }
      if (analyticsInstance) {
        modules.setAnalyticsCollectionEnabled(analyticsInstance, false);
      }
      clearFirebaseAnalyticsCookies();
      return;
    }

    let analytics = await getFirebaseAnalytics();
    if (!analytics && isFirebaseAnalyticsEnabled()) {
      analyticsInitPromise = null;
      analytics = await getFirebaseAnalytics();
    }
    if (analytics) {
      modules.setAnalyticsCollectionEnabled(analytics, true);
    }
  } catch {
    // Analytics is optional and must never interrupt the product.
  }
};
